import React from "react";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Cell,
  LabelList,
  CartesianGrid
} from "recharts";
import { formatNumber } from "@/utils/numbers";
import CustomTooltip from "./CustomTooltip";
import { TransformedChartData } from "@/components/layouts/PipelineOverTime/types";

export interface PipelineValueChartProps {
  chartData: TransformedChartData[];
  isDealsMetric: boolean;
  tooltipPos: { x: number; y: number };
  onMouseMove: (e: any) => void;
}

const PipelineValueChart: React.FC<PipelineValueChartProps> = ({
  chartData,
  isDealsMetric,
  tooltipPos,
  onMouseMove
}) => {
  return (
    <ResponsiveContainer width="100%" height={250}>
      <RechartsBarChart
        data={chartData}
        margin={{ left: 25, right: 25, top: 25, bottom: 5 }}
        onMouseMove={onMouseMove}
        barGap={-35}
      >
        <defs>
          <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#54F5BA" />
            <stop offset="100%" stopColor="#14C993" />
          </linearGradient>
          <linearGradient id="forecastedGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6759FF" />
            <stop offset="100%" stopColor="#4D43BF" />
          </linearGradient>
        </defs>

        <XAxis
          dataKey="date"
          interval={0}
          tickLine={false}
          axisLine={{ stroke: "var(--widget)" }}
          tick={(props) => {
            const { x, y, payload } = props;
            const dataPoint = chartData.find((d) => d.date === payload.value);

            const isCurrent = dataPoint?.isCurrent ?? false;

            return (
              <g>
                <text
                  x={x}
                  y={y + 8}
                  textAnchor="middle"
                  fill={isCurrent ? "var(--primary)" : "var(--secondary)"}
                  fontSize={12}
                  fontWeight={isCurrent ? "bold" : "normal"}
                >
                  {payload.value}
                </text>
                {isCurrent && (
                  <circle cx={x} cy={y + 20} r={2} fill="var(--primary)" />
                )}
              </g>
            );
          }}
        />
        <YAxis
          tickLine={false}
          axisLine={{ stroke: "var(--widget)" }}
          tick={{ fill: "var(--secondary)", fontSize: 12 }}
          domain={[
            (dataMin: number) => (dataMin < 0 ? dataMin * 1.15 : dataMin),
            (dataMax: number) => (dataMax > 0 ? dataMax * 1.15 : dataMax)
          ]}
          tickFormatter={(tick) => {
            if (isDealsMetric) {
              return tick.toString();
            }
            const formatted = formatNumber(tick, 2, true);
            return `${formatted.currencySign}${formatted.value}${formatted.modifier}`;
          }}
        />
        <CartesianGrid
          strokeDasharray="0"
          stroke="var(--grey-750)"
          vertical={false}
        />

        <Bar
          dataKey={isDealsMetric ? "actualDeals" : "actualPipeline"}
          fill="url(#actualGradient)"
          radius={[3, 3, 0, 0]}
          barSize={32}
        >
          {chartData.map((_entry, index) => (
            <Cell key={`cell-actual-${index}`} />
          ))}
          <LabelList
            dataKey={isDealsMetric ? "actualDeals" : "actualPipeline"}
            position="top"
            offset={10}
            formatter={(value: number) => {
              if (value === 0) return "";
              if (isDealsMetric) {
                return value.toString();
              }
              const formatted = formatNumber(value, 2, false);
              return `${formatted.value}${formatted.modifier}`;
            }}
            style={{
              fontSize: 12,
              fill: "var(--secondary)"
            }}
          />
        </Bar>
        <Bar
          dataKey={isDealsMetric ? "forecastedDeals" : "forecastedPipeline"}
          fill="url(#forecastedGradient)"
          radius={[3, 3, 0, 0]}
          barSize={32}
        >
          {chartData.map((_entry, index) => (
            <Cell key={`cell-forecasted-${index}`} />
          ))}
          <LabelList
            dataKey={isDealsMetric ? "forecastedDeals" : "forecastedPipeline"}
            position="top"
            offset={10}
            formatter={(value: number) => {
              if (value === 0) return "";
              if (isDealsMetric) {
                return value.toString();
              }
              const formatted = formatNumber(value, 2, false);
              return `${formatted.value}${formatted.modifier}`;
            }}
            style={{
              fontSize: 12,
              fill: "var(--secondary)"
            }}
          />
        </Bar>

        <RechartsTooltip
          content={<CustomTooltip />}
          cursor={{ fill: "transparent" }}
          position={tooltipPos}
          wrapperStyle={{
            visibility: tooltipPos.x ? "visible" : "hidden"
          }}
        />
      </RechartsBarChart>
    </ResponsiveContainer>
  );
};

export default PipelineValueChart;
